import {
  differenceBy,
  filter,
  find,
  forEach,
  get,
  groupBy,
  includes,
  isEmpty,
  isNil,
  keys,
  map,
  some,
  toUpper,
  values,
} from 'lodash'

import { mergeArrayOfObjects } from '../../core/utils'

import {
  INGREDIENT_KEYWORD_SUBTYPES,
  INGREDIENT_KEYWORD_TYPES,
} from '../../common/constants'
import { buildIngredientKeywordTree } from '../../common/helpers'
import { flattenTreeStructure } from '../../common/utils'

import buildSubtypeNodesForMovedOrTransferedNode from './build-subtype-nodes-for-moved-or-transfered-node'
import getKeywordsToTransfer from './get-keywords-to-transfer'
import getNodeToMoveTo from './get-node-to-move-to'
import getSubtypeNodesWithChildren from './get-subtype-nodes-with-children'
import transformListToTree from './transform-list-to-tree'

const moveKeywordsToNodeInSameIngredient = (
  mainKeywordsState,
  supportingKeywordsState,
) => {
  const flattenKeywordsGroupedByField = mergeArrayOfObjects(
    map(keys(mainKeywordsState.data), keywordType => ({
      [keywordType]: flattenTreeStructure(
        get(mainKeywordsState.data, keywordType),
      ),
    })),
  )

  const keywordsToTransfer = getKeywordsToTransfer(
    mainKeywordsState,
    flattenKeywordsGroupedByField,
  )

  const { nodeToMoveTo } = getNodeToMoveTo(
    supportingKeywordsState,
    flattenKeywordsGroupedByField,
  )

  const isNodeToMoveToAKeyword = includes([2, 4], nodeToMoveTo.level)

  if (isNodeToMoveToAKeyword) {
    throw new Error(
      "Can't transfer to level 3 or 5. Those levels are reserved for categories",
    )
  }

  const excludedSubtypeNodes = {}
  const transformedKeywords = {}

  forEach(keywordsToTransfer, keyword => {
    if (!isNil(get(excludedSubtypeNodes, keyword.id))) {
      return
    }

    const isParentTransfered = some(
      keywordsToTransfer,
      ({ id }) => id === keyword.parentId,
    )

    let children = []
    const levelToMoveTo = nodeToMoveTo.level + 1

    if (!isParentTransfered) {
      const keywordTypeToMoveTo = find(
        values(INGREDIENT_KEYWORD_TYPES),
        ({ id: nameOfKeywordType }) => nameOfKeywordType === nodeToMoveTo.type,
      )

      if (keyword.level === 6 && includes([2, 4], levelToMoveTo)) {
        children = buildSubtypeNodesForMovedOrTransferedNode(
          { ...keyword, level: levelToMoveTo },
          keywordTypeToMoveTo,
          nodeToMoveTo,
        )
      }

      if (keyword.level === 4) {
        if (levelToMoveTo === 6) {
          if (!isEmpty(getSubtypeNodesWithChildren(keyword))) {
            throw new Error(
              `The keyword ${keyword.term}, can't be moved to level ${levelToMoveTo} as it would lose children`,
            )
          }
          forEach(keyword.children, subtypeNode => {
            excludedSubtypeNodes[subtypeNode.id] = subtypeNode
          })
        }

        if (levelToMoveTo === 2) {
          children = buildSubtypeNodesForMovedOrTransferedNode(
            { ...keyword, level: levelToMoveTo },
            keywordTypeToMoveTo,
            nodeToMoveTo,
          )
        }

        if (levelToMoveTo === 4) {
          const availableChildrenKeySubtypesForOrigin = get(
            INGREDIENT_KEYWORD_SUBTYPES,
            `${keyword.subtype}.childrenKeyTypes`,
          )

          const availableChildrenKeySubtypesForDestination = get(
            INGREDIENT_KEYWORD_SUBTYPES,
            `${nodeToMoveTo.subtype}.childrenKeyTypes`,
          )

          const childrenSubtypesToCheckForChildren = differenceBy(
            availableChildrenKeySubtypesForOrigin,
            availableChildrenKeySubtypesForDestination,
            'id',
          )

          if (!isEmpty(childrenSubtypesToCheckForChildren)) {
            const subtypeNodesRestrictedFromTransfering =
              getSubtypeNodesWithChildren(
                keyword,
                childrenSubtypesToCheckForChildren,
              )

            if (!isEmpty(subtypeNodesRestrictedFromTransfering)) {
              throw new Error(
                `The keyword ${keyword.term}, can't be moved to level ${levelToMoveTo} as it would lose children`,
              )
            }
          }

          children = [
            ...buildSubtypeNodesForMovedOrTransferedNode(
              { ...keyword, level: levelToMoveTo },
              keywordTypeToMoveTo,
              nodeToMoveTo,
            ),
            ...(keyword.children || []),
          ]

          if (!isEmpty(childrenSubtypesToCheckForChildren)) {
            const keywordSubtypesToExclude = filter(children, ({ subtype }) =>
              some(
                childrenSubtypesToCheckForChildren,
                ({ id }) => id === subtype,
              ),
            )

            forEach(keywordSubtypesToExclude, subtypeNode => {
              excludedSubtypeNodes[subtypeNode.id] = subtypeNode
            })
          }
        }
      }

      if (keyword.level === 2) {
        if (levelToMoveTo === 6) {
          if (!isEmpty(getSubtypeNodesWithChildren(keyword))) {
            throw new Error(
              `The keyword ${keyword.term}, can't be moved to level ${levelToMoveTo} as it would lose children`,
            )
          }

          forEach(keyword.children, subtypeNode => {
            excludedSubtypeNodes[subtypeNode.id] = subtypeNode
          })
        }

        if (levelToMoveTo === 4) {
          const availableSubtypesForOrigin = values(INGREDIENT_KEYWORD_SUBTYPES)

          const availableChildrenKeySubtypesForDestination = get(
            INGREDIENT_KEYWORD_SUBTYPES,
            `${nodeToMoveTo.subtype}.childrenKeyTypes`,
          )

          const childrenSubtypesToCheckForChildren = differenceBy(
            availableSubtypesForOrigin,
            availableChildrenKeySubtypesForDestination,
            'id',
          )

          const subtypeNodesRestrictedFromTransfering =
            getSubtypeNodesWithChildren(
              keyword,
              childrenSubtypesToCheckForChildren,
            )

          if (!isEmpty(subtypeNodesRestrictedFromTransfering)) {
            throw new Error(
              `The keyword ${keyword.term}, can't be moved to level ${levelToMoveTo} as it would lose children`,
            )
          }

          const keywordSubtypesToExclude = filter(
            keyword.children,
            ({ subtype }) =>
              some(
                childrenSubtypesToCheckForChildren,
                ({ id }) => id === subtype,
              ),
          )

          forEach(keywordSubtypesToExclude, subtypeNode => {
            excludedSubtypeNodes[subtypeNode.id] = subtypeNode
          })
        }

        if (levelToMoveTo === 2 && nodeToMoveTo.type === keyword.type) {
          return
        }
      }
    }

    let subtype = isParentTransfered ? keyword.subtype : nodeToMoveTo.subtype

    const level = isParentTransfered
      ? get(transformedKeywords, `${keyword.parentId}.level`) + 1
      : levelToMoveTo

    if (level === 2) {
      subtype = null
    }

    transformedKeywords[keyword.id] = {
      ...keyword,
      isModified: true,
      children: isEmpty(children) ? keyword.children : children,
      level,
      type: nodeToMoveTo.type,
      subtype,
      parentId: isParentTransfered ? keyword.parentId : nodeToMoveTo.id,
    }

    forEach(children, child => {
      transformedKeywords[child.id] = child
    })
  })

  const transformedKeywordsGroupedByType = groupBy(
    values(transformedKeywords),
    'type',
  )

  return mergeArrayOfObjects(
    map(keys(flattenKeywordsGroupedByField), keywordType => {
      const flatKeywordsByKeywordType = get(
        flattenKeywordsGroupedByField,
        keywordType,
      )

      const unmodifiedKeywords = filter(flatKeywordsByKeywordType, keyword => {
        const transformedKeyword = get(transformedKeywords, keyword.id)
        return isNil(transformedKeyword)
      })

      const keywordsWithTransformedKeywords = [
        ...unmodifiedKeywords,
        ...get(transformedKeywordsGroupedByType, toUpper(keywordType), []),
      ]

      const keywordsWithoutExcludedSubtypes = filter(
        keywordsWithTransformedKeywords,
        keyword => {
          const excludedKeyword = get(excludedSubtypeNodes, keyword.id)
          return isNil(excludedKeyword)
        },
      )

      const keywordTree = transformListToTree(keywordsWithoutExcludedSubtypes)

      return {
        [keywordType]: buildIngredientKeywordTree({
          keywordTree,
          keywordType: find(
            values(INGREDIENT_KEYWORD_TYPES),
            ({ field }) => field === keywordType,
          ),
          skipCategoryCreation: true,
        }),
      }
    }),
  )
}

export default moveKeywordsToNodeInSameIngredient
